import { Select, Tooltip } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import propTypes from "prop-types";
import { api, AssetLocation, DualParkLaneFilter, IGetManualAllocationLanesParameters, IKmosGetManualAllocationLanesParameters, LaneAllocationSystem, PartialLaneType } from "../../../logic/api";
import { SessionContext } from "../../../utility/useSession";
import FloatingInputLabel from "../../../components/editable/EditableField/components/FloatingInputLabel";
import { useReleaseManagement, ReleaseFlag } from "../../../utility/feature-management";
import { ProgressView } from "../../../components/notifications/progress/ProgressView";
import { useTranslation } from "react-i18next";
import TabbedView from "../../../components/lists/TabbedView/TabbedView";
import TabbedViewPage from "../../../components/lists/TabbedView/TabbedViewPage";
import { IoMdInformationCircleOutline } from "react-icons/io";

function hasPartialType(location: AssetLocation): location is AssetLocation & { partialType: PartialLaneType } {
    return 'partialType' in location;
}

const VehicleAllocationLaneSelector: React.FunctionComponent<{
    label: string,
    value?: AssetLocation | null;
    params: IKmosGetManualAllocationLanesParameters;
    onChange: (value: AssetLocation) => void;
}> = (props) => {
    const session = useContext(SessionContext);
    const { t } = useTranslation();
    const setParams = useState<IKmosGetManualAllocationLanesParameters | IGetManualAllocationLanesParameters>({ ...props.params })[1];
    const [activeTab, setActiveTab] = useState<DualParkLaneFilter>(DualParkLaneFilter.Empty);

    const isSydneyDualParkAllocationReleased = useReleaseManagement(ReleaseFlag.releaseSydneyDualParkAllocation);
    const isDualParkSystemEnabled =
        isSydneyDualParkAllocationReleased &&
        session.current.site?.preferences.lane_allocation_system === LaneAllocationSystem.DualPark

    const oldKmosLocations = api.hooks.kmosV2.getLanesManualAllocation();
    const manualLocations = api.hooks.allocation.getManualAllocationLanes();

    const locations = isSydneyDualParkAllocationReleased
        ? manualLocations
        : oldKmosLocations;

    useEffect(() => {
        const carParkId = session.current.site?.home.id;
        if (carParkId) {
            const newParams = { ...props.params, carParkId };

            if (isDualParkSystemEnabled) {
                (newParams as IGetManualAllocationLanesParameters).filter = activeTab;
                (newParams as IGetManualAllocationLanesParameters).returnDate = props.params.returnDate;
            }

            setParams(newParams);
            locations.send(session.current.context, newParams);
        }
    }, [props.params, activeTab]);

    const handleTabChange = (tabIndex: number) => {
        setActiveTab(tabIndex);
    };

    const mapLocations = (locationsData: AssetLocation[]) => {
        // In partial tab when dual park system is enabled, group by risk level
        if (isDualParkSystemEnabled && activeTab === DualParkLaneFilter.Partial) {
            const okLanes: JSX.Element[] = [];
            const riskyLanes: JSX.Element[] = [];
            const blockedLanes: JSX.Element[] = [];

            locationsData.forEach((location, index) => {
                if (hasPartialType(location)) {
                    const option = laneOption(location, index);

                    switch (location.partialType) {
                        case PartialLaneType.Ok:
                            okLanes.push(option);
                            break;
                        case PartialLaneType.Risky:
                            riskyLanes.push(option);
                            break;
                        case PartialLaneType.Blocked:
                            blockedLanes.push(option);
                            break;
                    }
                }
            });

            return (
                <>
                    {okLanes.length > 0 && (
                        <optgroup label={t("laneAllocationSystem.labels.ok") || "OK"}>
                            {okLanes}
                        </optgroup>
                    )}
                    {riskyLanes.length > 0 && (
                        <optgroup label={t("laneAllocationSystem.labels.risky") || "Risky"}>
                            {riskyLanes}
                        </optgroup>
                    )}
                    {blockedLanes.length > 0 && (
                        <optgroup label={t("laneAllocationSystem.labels.blocked") || "Blocked"}>
                            {blockedLanes}
                        </optgroup>
                    )}
                </>
            );
        }

        // For non-partial tabs or non-dual park system
        return locationsData.map((locData, i) => laneOption(locData, i));
    }

    const laneOption = (location: AssetLocation, index: number) => {
        const { id, name, parentName, spaces } = location;

        return (
            <option
                key={index}
                value={id}
                title={name}
            >
                {t("kmos.labels.laneOption", { parentName, name, spaces })}
            </option>
        );
    }

    let selectedLocation: AssetLocation | null = null;

    if (props.value) {
        selectedLocation = props.value;
    }

    const allLocations = locations.data
        ? mapLocations(locations.data)
        : [];

    const renderSelector = (labelText: string, showRiskLevelInfo = false) => (
        <div style={{ maxHeight: "129px", display: "flex", alignItems: "center", flexDirection: "column", position: "relative" }}>
            <ProgressView status={locations.status}>
                <FloatingInputLabel label={labelText}>
                    <Select
                        native
                        style={{
                            border: "solid 1px lightgray",
                            width: 225,
                            borderRadius: 5,
                            padding: 5
                        }}
                        value={!selectedLocation ? -1 : selectedLocation.id}
                        onChange={(e) => {
                            if (locations.data) {
                                const matched = locations.data.find(l => l.id == e.target.value);
                                if (matched) {
                                    props.onChange(matched);
                                }
                            }
                        }}
                    >
                        <option value={-1} title=""></option>
                        {allLocations}
                    </Select>
                </FloatingInputLabel>

                {showRiskLevelInfo && (
                    <Tooltip
                        title={
                            <div>
                                <div><strong>{t("laneAllocationSystem.labels.laneRiskLevels")}</strong></div>
                                <div>{t("laneAllocationSystem.labels.okDescription")}</div>
                                <div>{t("laneAllocationSystem.labels.riskyDescription")}</div>
                                <div>{t("laneAllocationSystem.labels.blockedDescription")}</div>
                            </div>
                        }
                        placement="bottom"
                    >
                        <div style={{ position: 'absolute', top: 12, right: -30 }}>
                            <IoMdInformationCircleOutline size={20} color="#666" />
                        </div>
                    </Tooltip>
                )}
            </ProgressView>
        </div>
    );

    if (isDualParkSystemEnabled) {
        return (
            <div>
                <div style={{ minHeight: "129px" }}>
                    <TabbedView animated={false} selectedIndex={activeTab} onSelectedIndexChange={handleTabChange}>
                        {/* Empty Lanes Tab */}
                        <TabbedViewPage title={t("kmos.labels.empty")}>
                            {renderSelector(t("kmos.labels.selectLaneEmpty"))}
                        </TabbedViewPage>

                        {/* Partial Lanes Tab */}
                        <TabbedViewPage title={t("kmos.labels.partial")}>
                            {renderSelector(t("kmos.labels.selectLanePartial"), true)}
                        </TabbedViewPage>
                    </TabbedView>
                </div>
            </div>
        );
    }

    return (
        <ProgressView status={locations.status}>
            <FloatingInputLabel label={props.label}>
                <Select
                    native
                    style={{ border: "solid 1px lightgray", width: "100%", maxWidth: 225, borderRadius: 5, padding: 5 }}
                    value={!selectedLocation ? -1 : selectedLocation.id}
                    onChange={(e) => {
                        if (locations.data) {
                            const matched = locations.data.find(l => l.id == e.target.value);
                            if (matched) {
                                props.onChange(matched);
                            }
                        }
                    }}
                >
                    <option value={-1} title=""></option>
                    {allLocations}
                </Select>
            </FloatingInputLabel>
        </ProgressView>
    );
}

VehicleAllocationLaneSelector.propTypes = {
    label: propTypes.string.isRequired,
    value: propTypes.any,
    onChange: propTypes.func.isRequired,
    params: propTypes.any.isRequired,
}

export default VehicleAllocationLaneSelector;
